import * as Cesium from "cesium";
import { computeZoomLevel, disableCameraControls, restoreCameraControls } from "../MapUtility";

// Global registry per viewer (shared)
const REG_KEY = Symbol.for("cutDistanceRegistry");
const getRegistry = (viewer) => {
  if (!viewer[REG_KEY]) {
    viewer[REG_KEY] = { entities: new Set(), handlers: new Set(), keydowns: new Set(), batches: [] };
  }
  return viewer[REG_KEY];
};

// Draw, find 2D intersections (ignore Z), mark circles, label distances
export const handleDrawLineWithIntersections = (viewer, originalCameraControls, layerNames = [], { onDeactivate } = {}) => {
  const { ScreenSpaceEventHandler, ScreenSpaceEventType, CallbackProperty, Color, PropertyBag, Cartesian2, Cartesian3, Cartographic, Math: CesiumMath, JulianDate, EllipsoidGeodesic } = Cesium;

  const reg = getRegistry(viewer);
  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);
  reg.handlers.add(handler);

  // ---- state ----
  let isDrawing = false;
  let drawPts = [];
  let tempLine = null;
  let finalLine = null;

  // ---- camera lock ----
  let cameraLocked = false;
  const lockCamera = () => {
    if (!cameraLocked) {
      try {
        disableCameraControls(viewer, originalCameraControls);
      } catch {}
      cameraLocked = true;
    }
  };
  const unlockCamera = () => {
    if (cameraLocked) {
      try {
        restoreCameraControls(viewer, originalCameraControls);
      } catch {}
      cameraLocked = false;
    }
  };

  const makePerpOffset = (viewer, P, Q, px = 12) =>
    new CallbackProperty(() => {
      const sP = Cesium.SceneTransforms?.wgs84ToWindowCoordinates ? Cesium.SceneTransforms.wgs84ToWindowCoordinates(viewer.scene, P) : viewer.scene.cartesianToCanvasCoordinates(P);
      const sQ = Cesium.SceneTransforms?.wgs84ToWindowCoordinates ? Cesium.SceneTransforms.wgs84ToWindowCoordinates(viewer.scene, Q) : viewer.scene.cartesianToCanvasCoordinates(Q);
      if (!sP || !sQ) return new Cartesian2(0, -px);
      const vx = sQ.x - sP.x,
        vy = sQ.y - sP.y;
      const len = Math.hypot(vx, vy) || 1;
      const nx = -vy / len,
        ny = vx / len; // left-normal
      return new Cartesian2(nx * px, ny * px);
    }, false);

  // ---- utils ----
  const now = () => JulianDate.now();
  const isDef = (v) => v !== null && v !== undefined;
  const addEntity = (e, batchArr = null) => {
    reg.entities.add(e);
    if (Array.isArray(batchArr)) batchArr.push(e);
    return e;
  };
  const safeRemove = (e) => {
    if (!e) return;
    try {
      viewer.entities.remove(e);
    } catch {}
    reg.entities.delete(e);
  };

  const to2D = (cartesian) => {
    const c = Cartographic.fromCartesian(cartesian);
    return { x: CesiumMath.toDegrees(c.longitude), y: CesiumMath.toDegrees(c.latitude) };
  };
  const getMouseCartesian = (winPos) => {
    if (!winPos) return null;
    let c = viewer.scene.pickPosition(winPos);
    if (c) return c;
    c = viewer.camera.pickEllipsoid(winPos);
    if (c) return c;
    if (viewer.scene.globe) {
      const ray = viewer.camera.getPickRay(winPos);
      if (ray) {
        c = viewer.scene.globe.pick(ray, viewer.scene);
        if (c) return c;
      }
    }
    return null;
  };

  // intersection (lon/lat only)
  const EPS_DEG = 1e-7,
    EPS_PAR = 1e-12;
  const onSegEps = (x, y, a, b) => x >= Math.min(a.x, b.x) - EPS_DEG && x <= Math.max(a.x, b.x) + EPS_DEG && y >= Math.min(a.y, b.y) - EPS_DEG && y <= Math.max(a.y, b.y) + EPS_DEG;

  const intersect2DWithST = (A, B, C, D) => {
    const P1 = to2D(A),
      P2 = to2D(B);
    const Q1 = to2D(C),
      Q2 = to2D(D);
    const den = (P1.x - P2.x) * (Q1.y - Q2.y) - (P1.y - P2.y) * (Q1.x - Q2.x);
    if (!Number.isFinite(den) || Math.abs(den) < EPS_PAR) return null;

    const numx = P1.x * P2.y - P1.y * P2.x;
    const numy = Q1.x * Q2.y - Q1.y * Q2.x;
    const ix = (numx * (Q1.x - Q2.x) - (P1.x - P2.x) * numy) / den;
    const iy = (numx * (Q1.y - Q2.y) - (P1.y - P2.y) * numy) / den;
    if (!(onSegEps(ix, iy, P1, P2) && onSegEps(ix, iy, Q1, Q2))) return null;

    const dxAB = P2.x - P1.x,
      dyAB = P2.y - P1.y;
    const s = Math.abs(dxAB) >= Math.abs(dyAB) ? (ix - P1.x) / (dxAB || EPS_PAR) : (iy - P1.y) / (dyAB || EPS_PAR);
    const dxCD = Q2.x - Q1.x,
      dyCD = Q2.y - Q1.y;
    const t = Math.abs(dxCD) >= Math.abs(dyCD) ? (ix - Q1.x) / (dxCD || EPS_PAR) : (iy - Q1.y) / (dyCD || EPS_PAR);

    return { point: Cartesian3.fromDegrees(ix, iy, 0), s: Math.max(0, Math.min(1, s)), t: Math.max(0, Math.min(1, t)) };
  };

  const dedupeIntersections = (arr) => {
    const out = [];
    for (const it of arr) {
      const p = to2D(it.point);
      if (
        !out.some((o) => {
          const q = to2D(o.point);
          return Math.abs(p.x - q.x) <= EPS_DEG && Math.abs(p.y - q.y) <= EPS_DEG;
        })
      )
        out.push(it);
    }
    return out;
  };

  const surfaceDistanceMeters = (p1, p2) => {
    const c1 = Cartographic.fromCartesian(p1),
      c2 = Cartographic.fromCartesian(p2);
    const a = new Cartographic(c1.longitude, c1.latitude, 0);
    const b = new Cartographic(c2.longitude, c2.latitude, 0);
    return new EllipsoidGeodesic(a, b).surfaceDistance;
  };
  const fmtMeters = (m) => (!Number.isFinite(m) ? "" : `${m.toFixed(2)} m`);

  // target layers
  const clean = (s = "") =>
    String(s)
      .replace(/\.[^/.]+$/, "")
      .replace(/[\s\-_]/g, "")
      .toLowerCase();
  const targetDataSources = (() => {
    const all = viewer.dataSources?._dataSources || [];
    if (!layerNames?.length) return all;
    const wanted = layerNames.map(clean);
    return all.filter((ds) => wanted.some((w) => clean(ds.layerName || "").includes(w)));
  })();

  // only visible
  const isShown = (val, t) => {
    if (val === undefined) return true;
    if (typeof val?.getValue === "function") return !!val.getValue(t);
    return !!val;
  };

  const iterPolylineEntities = function* () {
    const T = now();
    for (const ds of targetDataSources) {
      if (!isShown(ds?.show, T)) continue;
      const vals = ds?.entities?.values || [];
      for (const e of vals) {
        if (!e?.polyline) continue;
        if (!isShown(e.show, T)) continue;
        if (!isShown(e.polyline.show, T)) continue;
        const pos = e.polyline.positions?.getValue?.(T);
        if (pos && pos.length >= 2) yield pos;
      }
    }
    const vvals = viewer.entities?.values || [];
    for (const e of vvals) {
      if (!e?.polyline) continue;
      if (e === finalLine || e === tempLine) continue;
      if (!isShown(e.show, T)) continue;
      if (!isShown(e.polyline.show, T)) continue;
      const pos = e.polyline.positions?.getValue?.(T);
      if (pos && pos.length >= 2) yield pos;
    }
  };

  // ---- key handling ----
  const isEditableTarget = (el) => {
    if (!el) return false;
    const n = (el.tagName || "").toLowerCase();
    return n === "input" || n === "textarea" || el.isContentEditable === true;
  };

  const undoLast = () => {
    if (isDrawing) {
      cleanupTemp();
      unlockCamera();
      return;
    }
    const batch = reg.batches.pop();
    if (!batch || !Array.isArray(batch.entities)) return;
    for (const e of batch.entities) safeRemove(e);
  };

  const onKeyDown = (e) => {
    if (isEditableTarget(e.target)) return;
    const key = e.key || e.code;
    if (key === "Backspace" || e.keyCode === 8) {
      e.preventDefault();
      undoLast();
    } else if (key === "Escape" || e.keyCode === 27) {
      e.preventDefault();
      hardDestroy();
    }
  };
  if (!reg.keydowns.has(onKeyDown)) {
    window.addEventListener("keydown", onKeyDown, true);
    reg.keydowns.add(onKeyDown);
  }

  // ---- zoom helpers ----
  const EARTH_CIRC = 40075016.68557849;
  const TILE = 256;
  const getMetersPerPixel = (viewer) => {
    const { scene } = viewer;
    const cx = scene.canvas.clientWidth / 2,
      cy = scene.canvas.clientHeight / 2;
    const pCenter = new Cartesian2(cx, cy),
      pRight = new Cartesian2(cx + 1, cy);
    const rayC = viewer.camera.getPickRay(pCenter),
      rayR = viewer.camera.getPickRay(pRight);
    const gC = scene.globe.pick(rayC, scene),
      gR = scene.globe.pick(rayR, scene);
    if (!gC || !gR) return Infinity;
    return Cartesian3.distance(gC, gR);
  };

  // ---- draw ----
  const start = (click) => {
    const p = getMouseCartesian(click.position);
    if (!isDef(p)) return;
    isDrawing = true;
    drawPts = [p, p];

    // no anchor during drawing

    tempLine = addEntity(
      viewer.entities.add({
        polyline: {
          positions: new CallbackProperty(() => drawPts, false),
          width: 2,
          material: Color.RED.withAlpha(0.9),
          clampToGround: true,
        },
        properties: new PropertyBag({ type: "draw_helper" }),
      }),
    );
    lockCamera();
  };

  const move = (movement) => {
    if (!isDrawing) return;
    const pos = getMouseCartesian(movement.endPosition || movement.startPosition);
    if (isDef(pos)) drawPts[1] = pos;
  };

  const end = (click) => {
    if (!isDrawing) return;
    isDrawing = false;

    const cand = getMouseCartesian(click.position);
    const endPos = isDef(cand) ? cand : drawPts[1];
    if (!isDef(endPos)) {
      cleanupTemp();
      return;
    }

    drawPts[1] = endPos;
    const A = Cartesian3.clone(drawPts[0]);
    const B = Cartesian3.clone(drawPts[1]);

    // compute minimal length based on screen scale
    const mpp = getMetersPerPixel(viewer); // meters per pixel
    const minMeters = Number.isFinite(mpp) && mpp > 0 ? mpp * 5 : 1; // ~5px
    const distMeters = surfaceDistanceMeters(A, B);

    // if user only clicked and didn't drag, abort and show nothing
    if (!Number.isFinite(distMeters) || distMeters < minMeters) {
      cleanupTemp(); // removes temp line and state
      unlockCamera();
      return;
    }

    if (tempLine) {
      safeRemove(tempLine);
      tempLine = null;
    }

    const batch = { entities: [] };

    // dashed final line
    const dashedMat = new Cesium.PolylineDashMaterialProperty({
      dashPattern: 0xff,
      dashLength: 16,
      color: Color.RED.withAlpha(0.9),
    });
    finalLine = addEntity(
      viewer.entities.add({
        polyline: { positions: [A, B], width: 2, material: dashedMat, clampToGround: false },
        properties: new PropertyBag({ type: "draw_final" }),
      }),
      batch.entities,
    );

    // show anchor ONLY after finish
    addEntity(
      viewer.entities.add({
        position: A,
        point: {
          pixelSize: 10,
          color: Color.ORANGERED, // start marker color
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        properties: new PropertyBag({ type: "anchor_final" }),
      }),
      batch.entities,
    );

    computeAndAnnotateIntersections(A, B, batch.entities);
    reg.batches.push(batch);

    unlockCamera();
  };

  // ---- intersections + annotations ----
  const computeAndAnnotateIntersections = (A, B, batchEntities) => {
    const hits = [];
    for (const positions of iterPolylineEntities()) {
      for (let i = 0; i < positions.length - 1; i++) {
        const h = intersect2DWithST(A, B, positions[i], positions[i + 1]);
        if (h) hits.push(h);
      }
    }
    const uniq = dedupeIntersections(hits).sort((a, b) => a.s - b.s);

    // dots
    uniq.forEach((h) => {
      addEntity(
        viewer.entities.add({
          position: h.point,
          point: {
            pixelSize: 6,
            color: Color.RED,
            outlineColor: Color.RED,
            outlineWidth: 2,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
          properties: new PropertyBag({ type: "intersection_dot" }),
        }),
        batchEntities,
      );
    });

    // labels with dynamic zoom rule
    if (uniq.length >= 2) {
      for (let i = 0; i < uniq.length - 1; i++) {
        const P = uniq[i].point,
          Q = uniq[i + 1].point;
        const mid = Cartesian3.lerp(P, Q, 0.5, new Cartesian3());
        const dist = surfaceDistanceMeters(P, Q);

        // big midpoint circle (always visible, constant size)
        // big midpoint circle (under the label)
        const perpOff = makePerpOffset(viewer, P, Q, 12);

        addEntity(
          viewer.entities.add({
            position: mid,
            point: {
              pixelSize: 18,
              color: Color.CYAN.withAlpha(0.95),
              outlineColor: Color.BLACK.withAlpha(0.8),
              outlineWidth: 2,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              show: true,
              pixelOffset: perpOff, // same direction as label, so it sits under it
            },
            properties: new PropertyBag({ type: "distance_mid_circle" }),
          }),
          batchEntities,
        );

        addEntity(
          viewer.entities.add({
            position: mid,
            label: {
              text: fmtMeters(dist),
              font: new CallbackProperty(() => {
                const z = computeZoomLevel(viewer);
                const base = 14;
                const add = z >= 23 ? 5 : 0;
                return `${base + add}px sans-serif`;
              }, false),
              pixelOffset: new CallbackProperty(() => {
                const o = perpOff.getValue(now());
                return new Cartesian2(o.x, o.y - 18); // lift label up a bit
              }, false),

              showBackground: true,
              backgroundColor: Color.BLACK.withAlpha(0.6),
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
              verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
              show: new CallbackProperty(() => {
                const z = computeZoomLevel(viewer);
                if (!Number.isFinite(z)) return true;
                const minZ = dist < 0.2 ? 26 : 21;
                return z >= minZ;
              }, false),
            },
            properties: new PropertyBag({ type: "distance_label" }),
          }),
          batchEntities,
        );
      }
    }
  };

  // ---- cleanup ----
  const cleanupTemp = () => {
    if (tempLine) safeRemove(tempLine);
    tempLine = null;
    drawPts = [];
  };

  const hardDestroy = () => {
    cleanupTemp();
    if (finalLine) {
      safeRemove(finalLine);
      finalLine = null;
    }

    for (const e of Array.from(reg.entities)) safeRemove(e);
    reg.entities.clear();
    reg.batches = [];

    try {
      handler.removeInputAction(ScreenSpaceEventType.LEFT_DOWN);
      handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
      handler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
    } catch {}
    try {
      handler.destroy();
    } catch {}
    reg.handlers.delete(handler);

    if (reg.keydowns.has(onKeyDown)) {
      window.removeEventListener("keydown", onKeyDown, true);
      reg.keydowns.delete(onKeyDown);
    }

    unlockCamera();
    try {
      if (typeof onDeactivate === "function") onDeactivate();
    } catch {}
  };

  // bind
  handler.setInputAction(start, ScreenSpaceEventType.LEFT_DOWN);
  handler.setInputAction(move, ScreenSpaceEventType.MOUSE_MOVE);
  handler.setInputAction(end, ScreenSpaceEventType.LEFT_UP);

  return { handler, destroy: hardDestroy, undoLast };
};
